import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// Show magic link for testing in development only
const DEBUG = import.meta.env.DEV;

export function Login() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'session_invalid') {
      setError('Sesiunea a expirat sau este invalidă. Vă rugăm să vă autentificați din nou.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMagicLink('');
    setIsLoading(true);

    try {
      const result = await login(email);
      if (DEBUG && result.link) {
        setMagicLink(result.link);
      } else {
        navigate('/auth/sent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare');
    } finally {
      setIsLoading(false);
    }
  };

  if (magicLink) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-serif text-ink mb-2">Link generat (mod dezvoltare)</h1>
          </div>

          <div className="space-y-4">
            <p className="text-muted">În producție, acest link ar fi trimis pe email.</p>
            
            <div className="mt-4 p-4 bg-warm rounded-lg">
              <p className="text-sm font-medium text-ink mb-2">Link magic:</p>
              <a 
                href={magicLink} 
                className="text-teal hover:underline break-all text-sm"
                onClick={() => navigate('/auth/sent')}
              >
                {magicLink}
              </a>
            </div>
            
            <p className="text-sm text-muted">Click pe link pentru a continua.</p>
          </div>

          <div className="mt-8">
            <Link to="/auth/login" className="text-teal hover:underline">
              ← Înapoi la autentificare
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-serif text-teal mb-2">BrainMap Partner Portal</h1>
          <p className="text-muted">Autentificare securizată cu Magic Link</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
              placeholder="nume@exemplu.ro"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-teal text-white font-medium rounded-lg hover:bg-teal-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Se trimite...' : 'Trimite link de acces'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-muted">
          <p>După ce trimiteți, verificați email-ul.</p>
          <p>Link-ul este valabil 15 minute.</p>
        </div>
      </div>
    </div>
  );
}
